import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useSEO } from '../../hooks/useSEO';
import { generateBreadcrumbSchema } from '../../utils/seo';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import Breadcrumb from '../../components/base/Breadcrumb';

interface Imovel {
  id: string;
  codigo: string;
  titulo: string;
  slug: string;
  tipo: string;
  cidade: string;
  bairro: string;
  preco: number;
  quartos: number;
  banheiros: number;
  vagas: number;
  area_total: number;
  area_construida: number;
  destaque: boolean;
  fotos: { url: string }[];
}

export default function ImoveisCidadePage() {
  const { cidade } = useParams<{ cidade: string }>();
  const [imoveis, setImoveis] = useState<Imovel[]>([]);
  const [loading, setLoading] = useState(true);

  const cidadeFormatada = cidade?.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || '';

  // SEO
  useSEO({
    title: `Imóveis em ${cidadeFormatada} - Sergipe | Mateus Oliveira Corretor`,
    description: `Encontre os melhores imóveis em ${cidadeFormatada}, Sergipe. Casas, apartamentos e terrenos de médio e alto padrão. Compra, venda e aluguel com atendimento especializado. WhatsApp (79) 99988-1002`,
    keywords: `imóveis ${cidadeFormatada}, casas ${cidadeFormatada}, apartamentos ${cidadeFormatada}, comprar imóvel ${cidadeFormatada}, vender imóvel ${cidadeFormatada}, corretor ${cidadeFormatada}`,
    canonical: window.location.href,
    schema: generateBreadcrumbSchema([
      { name: 'Imóveis', url: '/imoveis' },
      { name: cidadeFormatada, url: `/imoveis/${cidade}` },
    ]),
  });

  useEffect(() => {
    loadImoveis();
  }, [cidade]);

  const loadImoveis = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('imoveis')
        .select(`
          *,
          fotos:imovel_fotos(url)
        `)
        .eq('status', 'disponivel')
        .ilike('cidade', `%${cidadeFormatada}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImoveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb
            items={[
              { name: 'Imóveis', url: '/imoveis' },
              { name: cidadeFormatada, url: `/imoveis/${cidade}` },
            ]}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Imóveis em <span className="text-orange-500">{cidadeFormatada}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              Encontre os melhores imóveis de médio e alto padrão em {cidadeFormatada}, Sergipe. 
              Casas, apartamentos e terrenos com atendimento especializado e avaliação gratuita.
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : imoveis.length > 0 ? (
            <>
              <div className="mb-6">
                <p className="text-gray-600">
                  <strong>{imoveis.length}</strong> {imoveis.length === 1 ? 'imóvel encontrado' : 'imóveis encontrados'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-product-shop>
                {imoveis.map((imovel, index) => (
                  <motion.div
                    key={imovel.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      to={`/imovel/${imovel.slug}`}
                      className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer group"
                      data-product-shop
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300'}
                          alt={imovel.titulo}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                        {imovel.destaque && (
                          <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            Destaque
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-orange-500 uppercase">
                            {imovel.tipo}
                          </span>
                          <span className="text-xs text-gray-500">{imovel.codigo}</span>
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-orange-500 transition-colors line-clamp-2">
                          {imovel.titulo}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 flex items-center">
                          <i className="ri-map-pin-line mr-1"></i>
                          {imovel.bairro}, {imovel.cidade}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-3 pb-3 border-b">
                          <span className="flex items-center">
                            <i className="ri-hotel-bed-line mr-1"></i>
                            {imovel.quartos}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-shower-line mr-1"></i>
                            {imovel.banheiros}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-car-line mr-1"></i>
                            {imovel.vagas}
                          </span>
                          <span className="flex items-center">
                            <i className="ri-ruler-line mr-1"></i>
                            {imovel.area_total || imovel.area_construida}m²
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-orange-500">
                              {new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(imovel.preco)}
                            </p>
                          </div>
                          <button className="bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors text-xs whitespace-nowrap">
                            Ver Detalhes
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl">
              <i className="ri-home-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-600 mb-4">
                Nenhum imóvel encontrado em {cidadeFormatada}
              </p>
              <Link
                to="/imoveis"
                className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full transition-all cursor-pointer"
              >
                Ver Todos os Imóveis
              </Link>
            </div>
          )}

          {/* SEO Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-16 bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Por que comprar imóvel em {cidadeFormatada}?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p>
                {cidadeFormatada} é uma das cidades mais procuradas de Sergipe para quem busca qualidade de vida, 
                infraestrutura completa e excelentes oportunidades de investimento imobiliário.
              </p>
              <p>
                Com imóveis de médio e alto padrão, a região oferece apartamentos modernos, casas espaçosas 
                e terrenos em localização privilegiada. A Mateus Oliveira Imóveis é especialista no mercado 
                local e oferece atendimento personalizado para compra, venda e aluguel.
              </p>
              <p>
                Entre em contato pelo WhatsApp <strong>(79) 99988-1002</strong> e encontre o imóvel ideal 
                para você e sua família em {cidadeFormatada}.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <WhatsAppButton />
      <Footer />
    </>
  );
}
